import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { fmt, fmtDate } from '@dcc-erp/shared';
import type { OpContractAccrualRun } from '@dcc-erp/shared';
import { Icon } from '@dcc-erp/ui';

import { HttpError } from '../../lib/api';
import {
  useAccrualRun,
  useApproveAccrualRunStep,
  useRejectAccrualRunStep,
  useReturnAccrualRun,
} from '../../lib/queries/op-contracts';
import { AppModal } from '../AppModal';
import { ApprovalTimeline } from '../approval/ApprovalTimeline';
import { DecisionModal, type Decision } from '../approval/DecisionModal';
import { Can } from '../Can';

interface Props {
  companyId: string;
  /** The run list-row that opened this panel — used for the header/summary
   *  before the full detail (with the approvals chain) resolves. */
  run: OpContractAccrualRun;
  /** Display label for the period (e.g. "เม.ย. 2569"); falls back to run.period. */
  periodLabel?: string;
  onClose: () => void;
}

// OpContract monthly-RUN maker-checker panel (M3, docType OSC_RUN). Mirrors
// OpContractApprovalPanel but for the accrual-run header: there is exactly one
// approval per monthly run, and the bucket JVs post ONLY on final approval. The
// panel re-fetches the run detail so it has the materialized approvals chain
// even when the opener only had a list row, composes the shared ApprovalTimeline
// + DecisionModal, and gates the decision with <Can> (the borrowed apbill perms
// — backend authority is engine-enforced).
export function OpContractRunApprovalPanel({ companyId, run, periodLabel, onClose }: Props) {
  // Re-fetch the full detail (carries the approvals chain). Falls back to the
  // passed-in run row while loading.
  const { data: detail } = useAccrualRun(companyId, run.id);
  const view = detail ?? run;

  const approveMut = useApproveAccrualRunStep(companyId);
  const rejectMut = useRejectAccrualRunStep(companyId);
  const returnMut = useReturnAccrualRun(companyId);

  const [showDecide, setShowDecide] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const qc = useQueryClient();

  const decideBusy = approveMut.isPending || rejectMut.isPending || returnMut.isPending;

  const approvalStatus = view.approvalStatus ?? null;
  const currentStep = view.currentStep ?? 0;
  // The materialized chain lives only on the re-fetched detail; the list-row
  // fallback (`run`) has no `approvals`, so default to [] until the detail loads.
  const approvals = detail?.approvals ?? [];
  const totalSteps = approvals.length;
  const isPending = approvalStatus === 'PENDING';
  const label = periodLabel ?? view.period;

  async function handleDecide(d: Decision) {
    setActionError(null);
    try {
      if (d.action === 'approve') {
        await approveMut.mutateAsync({
          id: view.id,
          step: d.step,
          comment: d.comment,
          ...(d.sodOverrideReason ? { sodOverrideReason: d.sodOverrideReason } : {}),
        });
      } else if (d.action === 'return') {
        await returnMut.mutateAsync({
          id: view.id,
          step: d.step,
          reason: d.reason,
          ...(d.sodOverrideReason ? { sodOverrideReason: d.sodOverrideReason } : {}),
        });
      } else {
        await rejectMut.mutateAsync({
          id: view.id,
          step: d.step,
          reason: d.reason,
          ...(d.sodOverrideReason ? { sodOverrideReason: d.sodOverrideReason } : {}),
        });
      }
      setShowDecide(false);
      onClose();
    } catch (e) {
      if (e instanceof HttpError && e.status === 409) {
        setActionError('รายการนี้เปลี่ยนสถานะแล้ว — รีเฟรชข้อมูลให้แล้ว');
        void qc.invalidateQueries({ queryKey: ['op-contract-accrual-runs', companyId] });
      } else {
        setActionError(e instanceof Error ? e.message : 'เกิดข้อผิดพลาด');
      }
    }
  }

  return (
    <AppModal
      title={`สายอนุมัติการรับรู้รายเดือน — งวด ${label}`}
      subtitle={`${view.contractCount} สัญญา · รวม ${fmt(Number(view.totalAmount))} บาท`}
      onClose={onClose}
      footer={
        <>
          <button className="btn ghost" onClick={onClose}>
            ปิด
          </button>
          {isPending && (
            <Can permission="apbill.approve">
              <button
                className="btn primary"
                disabled={decideBusy}
                onClick={() => setShowDecide(true)}
              >
                ดำเนินการอนุมัติ
              </button>
            </Can>
          )}
        </>
      }
    >
      {/* RETURNED reason banner (resubmit context) */}
      {approvalStatus === 'RETURNED' && view.returnedReason && (
        <div
          style={{
            background: 'var(--warning-bg)',
            border: '1px solid var(--warning)',
            borderRadius: 6,
            padding: '10px 14px',
            fontSize: 13,
            marginBottom: 12,
          }}
          data-testid="osc-run-returned-reason"
        >
          <strong style={{ color: 'var(--warning)' }}>เหตุผลที่ส่งกลับ:</strong>{' '}
          {view.returnedReason}
        </div>
      )}

      {/* awaiting-step hint — the run posts its bucket JVs only on final approval */}
      {isPending && (
        <div className="meta" style={{ marginBottom: 12 }}>
          <Icon name="check" size={12} /> รออนุมัติการรับรู้ · ขั้น {currentStep}/{totalSteps} · JV
          จะถูกโพสต์เมื่ออนุมัติขั้นสุดท้าย
        </div>
      )}

      <ApprovalTimeline
        approvals={approvals}
        isActive={isPending}
        currentStep={currentStep}
        emptyText="ยังไม่ส่งอนุมัติ"
        leadLine={
          view.submittedBy ? (
            <div className="meta">
              ส่งอนุมัติโดย {view.submittedBy}
              {view.submittedAt ? ` · ${fmtDate(view.submittedAt)}` : ''}
            </div>
          ) : undefined
        }
        trailLine={
          approvalStatus === 'APPROVED' && view.approvedBy ? (
            <div className="meta">
              อนุมัติโดย {view.approvedBy}
              {view.approvedAt ? ` · ${fmtDate(view.approvedAt)}` : ''}
            </div>
          ) : undefined
        }
      />

      {actionError && (
        <div
          style={{ color: 'var(--negative)', fontSize: 14, marginTop: 10 }}
          data-testid="osc-run-action-error"
        >
          {actionError}
        </div>
      )}

      {showDecide && (
        <DecisionModal
          voucherNo={`งวด ${label}`}
          step={currentStep || 1}
          busy={decideBusy}
          error={actionError}
          titlePrefix="พิจารณาการรับรู้รายเดือน"
          onConfirm={(d) => {
            void handleDecide(d);
          }}
          onClose={() => {
            setShowDecide(false);
            setActionError(null);
          }}
        />
      )}
    </AppModal>
  );
}
